import { NgComponentOutlet } from "@angular/common";
import { Component, inject, InjectionToken, Injector, input, output, Type } from "@angular/core";
import { MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";

import { TimelineDetailBase } from "../timeline.model";

/** Lets a details info component select another detail (e.g. a build in a list) or close the card. */
export interface TimelineDetailsContext {
  select(detailId: string): void;
  close(): void;
}

export const TIMELINE_DETAILS_CONTEXT = new InjectionToken<TimelineDetailsContext>("TimelineDetailsContext");

/**
 * The Details card shell: header (kind, title, sub) and close button, with the data set's info component below it.
 * The info component gets the selected detail as its `detail` input and can inject `TIMELINE_DETAILS_CONTEXT`.
 */
@Component({
  selector: "lib-timeline-details-card",
  imports: [MatIconButton, MatIcon, NgComponentOutlet],
  template: `
    @if (detail(); as detail) {
      <div class="head">
        <div class="head-text">
          <div class="kind">{{ detail.kind }}</div>
          <h2 class="title">{{ detail.title }}</h2>
          @if (detail.sub) {
            <div class="sub">{{ detail.sub }}</div>
          }
        </div>
        @if (closable()) {
          <button
            mat-icon-button
            type="button"
            aria-label="Close details"
            (click)="closed.emit()"
          >
            <mat-icon>close</mat-icon>
          </button>
        }
      </div>
      @if (component(); as component) {
        <ng-container
          [ngComponentOutlet]="component"
          [ngComponentOutletInputs]="{ detail }"
          [ngComponentOutletInjector]="injector"
        />
      }
    } @else {
      <p class="empty">{{ emptyText() }}</p>
    }
  `,
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      gap: 12px;
      min-width: 0;
    }

    .head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 8px;
    }

    .head-text {
      min-width: 0;
    }

    .kind {
      font-size: 12px;
      font-weight: 500;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: #616161;
    }

    .title {
      margin: 2px 0 0;
      font-size: 18px;
      font-weight: 500;
    }

    .sub {
      font-size: 13px;
      color: #616161;
    }

    .empty {
      margin: 0;
      font-size: 13px;
      color: #616161;
    }
  `,
})
export class TimelineDetailsCard<TDetail extends TimelineDetailBase = TimelineDetailBase> {
  readonly detail = input<TDetail | null>(null);
  /** Renders the detail's content; takes a `detail` input. */
  readonly component = input<Type<unknown> | null>(null);
  readonly emptyText = input("Select an entry to see its details.");
  /** Hide the close button when the container (side or bottom sheet) has its own. */
  readonly closable = input(true);
  readonly selectDetail = output<string>();
  readonly closed = output<void>();

  protected readonly injector = Injector.create({
    providers: [
      {
        provide: TIMELINE_DETAILS_CONTEXT,
        useValue: {
          select: (id: string) => this.selectDetail.emit(id),
          close: () => this.closed.emit(),
        } satisfies TimelineDetailsContext,
      },
    ],
    parent: inject(Injector),
  });
}
